<template>
  <section class="change-history-view">
    <AdminHeader
      :texts="{ title: $t('change_history') }"
      :isSearchShown="false"
      :isAddShown="false"
    >
      <TableSearch
        :tableTitles="tableTitles"
        @input="handleInput"
      />
    </AdminHeader>

    <Loading v-if="isLoading" />

    <template v-if="!isLoading && rows.length">
      <div class="history-table">
        <div class="table-header">
          <p class="col-when">{{ $t('change_datetime') }}</p>
          <p class="col-client">{{ $t('change_client_site') }}</p>
          <p class="col-user">{{ $t('change_user') }}</p>
          <p class="col-field">{{ $t('change_field') }}</p>
          <p class="col-old">{{ $t('change_old_value') }}</p>
          <p class="col-new">{{ $t('change_new_value') }}</p>
          <p class="col-source">{{ $t('change_source') }}</p>
        </div>

        <div
          v-for="row in rows"
          :key="row.id"
          class="table-row"
        >
          <p class="col-when">{{ formatDateTime(row.created_at) }}</p>
          <p class="col-client table-cell text-cell">
            <span class="primary">{{ row.company_name || '—' }}</span>
            <span class="secondary">{{ row.site_name || '—' }}</span>
            <span v-if="row.entity_label" class="secondary">{{ row.entity_label }}</span>
          </p>
          <p class="col-user table-cell text-cell">
            <span class="primary">{{ row.actor_name || '—' }}</span>
            <span class="secondary">{{ row.actor_email || '' }}</span>
          </p>
          <p class="col-field table-cell text-cell">{{ formatField(row.field_name) }}</p>
          <p class="col-old table-cell text-cell">{{ displayValue(row.old_value) }}</p>
          <p class="col-new table-cell text-cell">{{ displayValue(row.new_value) }}</p>
          <p class="col-source">
            <span class="source-badge" :class="row.change_source">
              {{ row.change_source === 'automatic' ? $t('change_source_automatic') : $t('change_source_manual') }}
            </span>
          </p>
        </div>
      </div>

      <div class="pagination-container" v-if="total > 0 && totalPages > 1">
        <div class="pagination-info">
          {{ $t('showing') }} {{ paginationStart }}-{{ paginationEnd }} {{ $t('of') }} {{ total }}
        </div>
        <div class="pagination-controls">
          <label class="per-page-label">
            {{ $t('per_page') }}:
            <select v-model="itemsPerPage" class="per-page-select" @change="handlePerPageChange">
              <option v-for="opt in itemsPerPageOptions" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </label>
          <button
            type="button"
            class="pagination-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            {{ $t('previous') }}
          </button>
          <div class="pagination-pages">
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="pagination-page"
              :class="{ active: currentPage === page }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </div>
          <button
            type="button"
            class="pagination-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            {{ $t('next') }}
          </button>
        </div>
      </div>
    </template>

    <div v-if="!isLoading && !rows.length && hasActiveSearch" class="no-results">
      <p>{{ $t('no_results_found') }}</p>
    </div>
    <div v-if="!isLoading && !rows.length && !hasActiveSearch" class="no-results">
      <h3>{{ $t('no_change_history') }}</h3>
    </div>
  </section>
</template>

<script>
import AdminHeader from '../components/AdminHeader'
import Loading from '@/modules/common/components/Loading'
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import changeHistoryService from '../services/changeHistoryService'
import { Toast } from '@/modules/common/Toast'

export default {
  name: 'ChangeHistory',
  components: {
    AdminHeader,
    Loading,
    TableSearch,
  },
  data() {
    return {
      rows: [],
      total: 0,
      isLoading: false,
      searchTxt: '',
      searchBy: 'company_name',
      currentPage: 1,
      itemsPerPage: 25,
      itemsPerPageOptions: [10, 25, 50, 100],
      tableTitles: [
        'company_name',
        'site_name',
        'actor_name',
        'field_name',
        'entity_label',
        'change_source',
      ],
    }
  },
  computed: {
    totalPages() {
      if (!this.total) return 0
      return Math.ceil(this.total / this.itemsPerPage)
    },
    paginationStart() {
      if (!this.total) return 0
      return (this.currentPage - 1) * this.itemsPerPage + 1
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.total)
    },
    hasActiveSearch() {
      return !!this.searchTxt
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }
      const pages = new Set([1, total, current, current - 1, current + 1])
      return [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)
    },
  },
  created() {
    const user = this.$store && this.$store.getters && this.$store.getters.loggedUser
    if (!user || user.level !== 'buzzz') {
      this.$router.replace('/admin/site')
      return
    }
    this.loadHistory()
  },
  methods: {
    async loadHistory() {
      this.isLoading = true
      try {
        const result = await changeHistoryService.getHistory({
          page: this.currentPage,
          limit: this.itemsPerPage,
          search: this.searchTxt || undefined,
          searchBy: this.searchTxt ? this.searchBy : undefined,
          entityType: 'shift',
        })
        this.rows = (result && result.rows) || []
        this.total = (result && result.total) || 0
      } catch (err) {
        this.rows = []
        this.total = 0
        new Toast({
          message: this.$t('error_loading_change_history'),
          type: 'error',
          duration: 4000,
        })
      } finally {
        this.isLoading = false
      }
    },
    handleInput(value, searchBy) {
      if (searchBy) this.searchBy = searchBy
      this.searchTxt = value
      this.reload()
    },
    reload() {
      this.currentPage = 1
      this.loadHistory()
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.loadHistory()
    },
    handlePerPageChange() {
      this.currentPage = 1
      this.loadHistory()
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString()
    },
    formatField(fieldName) {
      if (!fieldName) return '—'
      const key = `change_field_${fieldName}`
      const translated = this.$t(key)
      return translated === key ? fieldName : translated
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      return value
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/vars.scss' as *;

.change-history-view {
  padding-bottom: 24px;
}

.history-table {
  padding: 0 16px;
  overflow-x: auto;
}

.table-header {
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1.2fr 1.1fr 1fr 1fr 0.9fr;
  width: 100%;
  border-bottom: 1px solid #e7e7e7;
  background: #fafafa;
  font-weight: $font-weight-bold;
  color: #606060;
  text-transform: uppercase;
  font-size: 12px;

  p {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    margin: 0;
  }
}

.table-row {
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1.2fr 1.1fr 1fr 1fr 0.9fr;
  width: 100%;
  border-bottom: 1px solid #e7e7e7;
  min-height: 5vh;
  align-items: center;

  &:hover {
    background-color: #f5fafe;
  }

  p {
    margin: 0;
    padding: 12px 16px;
    font-size: $font-size-regular;
    color: #333;
  }

  .table-cell {
    overflow: hidden;

    &.text-cell {
      display: flex;
      flex-direction: column;
      gap: 2px;
      word-break: break-word;
    }
  }
}

.primary {
  font-weight: 600;
}

.secondary {
  color: #757575;
  font-size: 12px;
}

.source-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;

  &.manual {
    background: #e3f2fd;
    color: #1565c0;
  }

  &.automatic {
    background: #f3e5f5;
    color: #6a1b9a;
  }
}

.pagination-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  font-size: $font-size-regular;
}

.pagination-info {
  color: #606060;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.per-page-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #606060;

  .per-page-select {
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: $white;
    font-size: $font-size-regular;
    cursor: pointer;
  }
}

.pagination-btn,
.pagination-page {
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: $white;
  font-size: $font-size-regular;
  cursor: pointer;
  color: #333;

  &:hover:not(:disabled) {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-page {
  min-width: 36px;
  padding: 8px 10px;

  &.active {
    background: #1976d2;
    border-color: #1976d2;
    color: white;
  }
}

.no-results {
  text-align: center;
  padding: 60px 20px;
  color: $dark-grey;

  h3 {
    font-size: 1.5rem;
    margin: 0 0 12px 0;
    color: $black-blue;
    font-weight: $font-weight-bold;
  }

  p {
    font-size: 1.1rem;
    margin: 0;
  }
}
</style>
